// Cookie consent storage + helpers.
// Single source of truth for consent state across the app.

export const CONSENT_STORAGE_KEY = "reserve.cookie-consent";
export const CONSENT_VERSION = 2;
export const CONSENT_EVENT = "reserve:cookie-consent-changed";

export type CookieCategory = "necessary" | "functional" | "analytics" | "marketing";

export type CookiePreferences = Record<CookieCategory, boolean>;

export type ConsentRecord = {
  version: number;
  preferences: CookiePreferences;
  updatedAt: string;
};

export const ALL_CATEGORIES: {
  key: CookieCategory;
  label: string;
  required?: boolean;
  description: string;
}[] = [
  {
    key: "necessary",
    label: "Strictly necessary",
    required: true,
    description:
      "Required for authentication, security, load balancing and CSRF protection. Cannot be disabled.",
  },
  {
    key: "functional",
    label: "Functional",
    description:
      "Remembers preferences such as notification settings, saved filters and display options.",
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Aggregated, pseudonymised usage statistics that help us improve the Platform.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Used to measure the effectiveness of campaigns and personalise communications.",
  },
];

export const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const ALL_ENABLED: CookiePreferences = {
  necessary: true,
  functional: true,
  analytics: true,
  marketing: true,
};

export function readConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord> & {
      choice?: "accepted" | "necessary";
    };
    // Migrate legacy v1 shape: { choice, at }
    if (!parsed.version && parsed.choice) {
      return {
        version: CONSENT_VERSION,
        preferences:
          parsed.choice === "accepted" ? ALL_ENABLED : DEFAULT_PREFERENCES,
        updatedAt:
          (parsed as { at?: string }).at ?? new Date().toISOString(),
      };
    }
    if (!parsed.preferences) return null;
    return {
      version: parsed.version ?? CONSENT_VERSION,
      preferences: { ...DEFAULT_PREFERENCES, ...parsed.preferences },
      updatedAt: parsed.updatedAt ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function saveConsent(preferences: CookiePreferences): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    preferences: { ...preferences, necessary: true },
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: record }),
    );
  } catch {
    /* storage blocked — ignore */
  }
  // Defensible server-side audit trail (ICO). Fire-and-forget; never block UI.
  void persistConsentToServer(record);
  return record;
}

function getAnonymousId(): string {
  const key = "reserve.anon-id";
  try {
    let id = window.localStorage.getItem(key);
    if (!id) {
      id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).toString();
      window.localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

async function persistConsentToServer(record: ConsentRecord) {
  if (typeof window === "undefined") return;
  try {
    const { logCookieConsent } = await import("@/lib/compliance.functions");
    await logCookieConsent({
      data: {
        preferences: record.preferences,
        version: record.version,
        anonymous_id: getAnonymousId(),
      },
    });
  } catch {
    /* offline / blocked — local record still stands */
  }
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
  } catch {
    /* ignore */
  }
}

export function hasConsent(category: CookieCategory): boolean {
  const record = readConsent();
  if (!record) return category === "necessary";
  return Boolean(record.preferences[category]);
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("reserve:open-cookie-preferences"));
}
